/**
 * Google Identity Services (GIS) - gets a real Google ID token for
 * POST /api/auth/sso-login, which avrio-backend's SsoTokenVerifier verifies
 * against Google's own JWKS before trusting a single claim out of it.
 *
 * WHY google.accounts.id.prompt(), NOT a custom button's own click handler
 * calling some imperative "sign in" method. GIS genuinely has no such
 * method - its credential flow is tied to either (a) a button it renders
 * and controls itself (google.accounts.id.renderButton), which this app's
 * SsoButtons deliberately does not use so the existing hand-matched Google
 * branding stays exactly as specified, or (b) prompt(), the One Tap /
 * "automatic sign-in" UI, which - unlike window.open-based OAuth popups -
 * is an iframe overlay the already-loaded GIS script renders directly, not
 * a new browser window, so it is not blocked by popup-blocker heuristics
 * the way a naive window.open from an async callback would be. Calling it
 * from this app's own button's onClick is the documented, supported
 * integration point for "I want my own button, not Google's rendered one."
 *
 * The real limitation this brings, stated rather than hidden: prompt() can
 * silently decline to show anything (third-party cookies blocked, the
 * browser already dismissed it recently, etc.) - signInWithGoogle() surfaces
 * that as a real, distinguishable rejection rather than a promise that
 * never settles.
 */

const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

interface CredentialResponse {
  credential: string;
}

interface PromptMomentNotification {
  isNotDisplayed(): boolean;
  isSkippedMoment(): boolean;
  isDismissedMoment(): boolean;
  getNotDisplayedReason(): string;
  getSkippedReason(): string;
  getDismissedReason(): string;
}

interface GoogleAccountsId {
  initialize(config: {
    client_id: string;
    callback: (response: CredentialResponse) => void;
  }): void;
  prompt(momentListener: (notification: PromptMomentNotification) => void): void;
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleAccountsId } };
  }
}

export class GoogleSignInUnavailableError extends Error {}

let scriptLoadPromise: Promise<void> | null = null;
let initialized = false;

const loadScript = (): Promise<void> => {
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new GoogleSignInUnavailableError('Could not load Google Sign-In.'));
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
};

/**
 * Resolves with a real Google ID token (JWT), or rejects with
 * GoogleSignInUnavailableError if the One Tap UI could not be shown at all
 * (not the same as the person closing it without choosing an account -
 * that case is also a rejection, but it's the person's own choice, not an
 * availability problem - both are surfaced as the same error type here
 * since the caller's only correct response to either is the same generic
 * "sign-in didn't complete" message).
 */
export const signInWithGoogle = (clientId: string): Promise<string> =>
  loadScript().then(
    () =>
      new Promise<string>((resolve, reject) => {
        const accountsId = window.google?.accounts.id;
        if (!accountsId) {
          reject(new GoogleSignInUnavailableError('Google Sign-In did not load.'));
          return;
        }

        if (!initialized) {
          accountsId.initialize({
            client_id: clientId,
            callback: (response) => resolve(response.credential),
          });
          initialized = true;
        }

        accountsId.prompt((notification) => {
          // A successful sign-in ALSO fires this listener, as a "dismissed"
          // moment with reason "credential_returned" - not just the user
          // closing the prompt. Checking isDismissedMoment() alone would
          // race the real credential callback above: whichever of resolve/
          // reject this promise sees first wins, since a settled promise
          // ignores every call after. Excluding this one reason is what
          // keeps a successful sign-in from being decided by that race
          // instead of by the credential actually arriving.
          if (
            notification.isDismissedMoment() &&
            notification.getDismissedReason() === 'credential_returned'
          ) {
            return;
          }
          if (
            notification.isNotDisplayed() ||
            notification.isSkippedMoment() ||
            notification.isDismissedMoment()
          ) {
            reject(
              new GoogleSignInUnavailableError(
                'Google Sign-In was closed or could not be shown. Please try again.',
              ),
            );
          }
          // Otherwise wait - the prompt is still displayed, nothing to do yet.
        });
      }),
  );
